import { useState } from 'react';
import Button from '../../../reusable/Button/Button';
import Input from '../../../reusable/Input/Input';
import SegmentedTabs from '../../../reusable/SegmentedTabs/SegmentedTabs';
import Select from '../../../reusable/Select/Select';
import Skeleton from '../../../reusable/Skeleton/Skeleton';
import StatusChip from '../../../reusable/StatusChip/StatusChip';
import { toast } from '../../../reusable/Toast/toastStore';
import { authErrorMessage } from '../../../store/api/authHelpers';
import {
  useGetPaymentVerificationQuery,
  useUpdatePaymentVerificationMutation,
} from '../../../store/api/paymentVerificationApi';
import { countryName, DEFAULT_COUNTRY_CODE, findCountry } from '../../../utilities/country';
import { PAYMENT_METHODS } from '../data';
import MethodLogo from './MethodLogo';
import { t } from '../../../i18n/i18n';
import './PaymentVerification.scss';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const methodById = (id) => PAYMENT_METHODS.find((method) => method.id === id);

/** Bank Wire's rows depend on the rail picked; every other method has one form. */
const fieldsOf = (method, transfer) =>
  method.transfers
    ? (method.transfers.find((t) => t.id === transfer) ?? method.transfers[0]).fields
    : method.fields;

/** Rows that hold a value — headings and notices are layout only. */
const isValue = (field) => Boolean(field.name);

/** What a row reads before the trader touches it. */
const initialOf = (field) =>
  field.initial ?? (field.kind === 'country' ? DEFAULT_COUNTRY_CODE : '');

const valueOf = (field, values) => values[field.name] ?? initialOf(field);

/** The method on file, by the API's `verification_type`. Unknown types read as none. */
const methodOf = (payment) =>
  payment?.verification_type
    ? PAYMENT_METHODS.find((method) => method.verification_type === payment.verification_type)?.id
    : undefined;

/** `bank_wire` is the rail a Bank Wire payout rides — "ACH" → ach. Null for other methods. */
const transferOf = (payment) => payment?.bank_wire?.toLowerCase() ?? 'ach';

const MASKED = new Set(['account_number']);

/** How a saved value prints in the summary. Account numbers show their tail only. */
const shown = (field, value) => {
  if (!value) return '—';
  if (field.kind === 'country') return countryName(value);
  if (field.kind === 'radio') return t(field.options.find((o) => o.value === value)?.label ?? value);
  if (MASKED.has(field.name)) return `•••• ${String(value).replace(/\s/g, '').slice(-4)}`;
  return value;
};

const validate = (fields, values) => {
  const errors = {};
  fields.filter(isValue).forEach((field) => {
    const value = String(valueOf(field, values)).trim();
    if (!value) {
      if (!field.optional) errors[field.name] = t('{{label}} is required', { label: t(field.label) });
    } else if (field.type === 'email' && !EMAIL.test(value)) {
      errors[field.name] = t('Enter a valid email');
    } else if (field.digits && !new RegExp(`^\\d{${field.digits}}$`).test(value)) {
      errors[field.name] = t('Must be {{count}} digits', { count: field.digits });
    } else if (field.minLength && value.replace(/\s/g, '').length < field.minLength) {
      errors[field.name] = t('That looks too short for an account number');
    }
  });
  return errors;
};

const HOW_IT_WORKS = [
  {
    title: 'Pick a payout method',
    desc: 'Rise is the fastest. Wise and bank wire pay out in your local currency.',
  },
  {
    title: 'We check the details',
    desc: 'The account name must match your verified identity. Most checks clear within 1 business day.',
  },
  {
    title: 'Every payout goes there',
    desc: 'One method covers every account you hold. Change it any time before you request a payout.',
  },
];

/** One form row — an input, the country picker, a radio group, or layout. */
function FieldRow({ field, values, errors, onChange }) {
  if (field.kind === 'heading') {
    return <h3 className="pay-fields__heading">{t(field.label)}</h3>;
  }

  if (field.kind === 'notice') {
    return (
      <div className="pay-notice" role="note">
        <span className="pay-notice__icon" aria-hidden="true">
          !
        </span>
        <p>{t(field.text)}</p>
      </div>
    );
  }

  const value = valueOf(field, values);
  const error = errors[field.name];
  const span = field.half ? 'pay-fields__cell is-half' : 'pay-fields__cell';

  if (field.kind === 'country') {
    return (
      <div className={span}>
        <Select
          label={t(field.label)}
          name={field.name}
          // The payload holds the name ("India"); the picker holds the code.
          value={findCountry(value)?.code ?? value}
          onChange={onChange(field.name)}
          error={t(error)}
          required
        />
      </div>
    );
  }

  if (field.kind === 'radio') {
    return (
      <fieldset className="pay-radios">
        <legend className="pay-radios__legend">{t(field.label)}</legend>
        {field.options.map((option) => (
          <label key={option.value} className="pay-radios__option">
            <input
              type="radio"
              name={field.name}
              value={option.value}
              checked={value === option.value}
              onChange={onChange(field.name)}
            />
            <span>{t(option.label)}</span>
          </label>
        ))}
      </fieldset>
    );
  }

  return (
    <div className={span}>
      <Input
        name={field.name}
        type={field.type ?? 'text'}
        label={t(field.label)}
        placeholder={t(field.placeholder)}
        hint={error ? undefined : t(field.hint)}
        value={value}
        onChange={onChange(field.name)}
        error={t(error)}
        required={!field.optional}
        inputMode={field.digits ? 'numeric' : undefined}
      />
    </div>
  );
}

/** The two cards, drawn in the page's own classes while the method on file loads. */
function PaymentSkeleton() {
  return (
    <>
      <section className="pay-card" aria-busy="true" aria-label={t('Loading payout method')}>
        <div className="pay-card__head">
          <div>
            <Skeleton w="120px" h={14} />
          </div>
        </div>
        <div className="pay-methods">
          {PAYMENT_METHODS.map((item) => (
            <span key={item.id} className="pay-method">
              <Skeleton w="72px" h={24} />
              <Skeleton w="80%" h={12} />
              <Skeleton w="60%" h={10} />
            </span>
          ))}
        </div>
      </section>

      <section className="pay-columns">
        <div className="pay-card pay-details" aria-hidden="true">
          <Skeleton w="160px" h={14} />
          <Skeleton w="100%" h={40} />
        </div>
      </section>
    </>
  );
}

/**
 * The picker and the details form. Its state seeds from what is on file, so it
 * only mounts once the read has landed.
 */
function PaymentForm({ payment }) {
  const [updatePayment, { isLoading: isSaving }] = useUpdatePaymentVerificationMutation();
  const savedMethod = methodOf(payment);
  const savedDetails = payment?.details ?? {};
  const savedTransferId = transferOf(payment);

  const [selected, setSelected] = useState(savedMethod ?? PAYMENT_METHODS[0].id);
  const [transfer, setTransfer] = useState(savedTransferId);
  const [values, setValues] = useState(savedDetails);
  const [errors, setErrors] = useState({});
  const [editing, setEditing] = useState(!payment);

  const method = methodById(selected);
  const fields = fieldsOf(method, transfer);
  const onFile = savedMethod === selected;
  // A method other than the one on file is always a fresh form.
  const readOnly = onFile && !editing;

  // The summary always describes what is saved, which may be another rail.
  const savedFields = onFile ? fieldsOf(method, savedTransferId) : [];
  const savedTransfer = method.transfers?.find((t) => t.id === savedTransferId);

  const choose = (id) => {
    if (methodById(id).comingSoon || id === selected) return;
    const returning = savedMethod === id;
    setSelected(id);
    setErrors({});
    // Returning to the method on file brings its saved details back.
    setValues(returning ? savedDetails : {});
    setTransfer(returning ? savedTransferId : 'ach');
    setEditing(!returning);
  };

  const change = (name) => (event) => {
    const { value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const switchTransfer = (id) => {
    setTransfer(id);
    // The shared rows keep their values; the errors belong to the old rail.
    setErrors({});
  };

  const cancel = () => {
    setErrors({});
    if (payment) {
      setSelected(savedMethod);
      setValues(savedDetails);
      setTransfer(savedTransferId);
      setEditing(false);
    } else {
      setValues({});
    }
  };

  // The update refreshes the read, which is what the aside reads too.
  const save = async (event) => {
    event.preventDefault();
    if (isSaving) return;
    const found = validate(fields, values);
    setErrors(found);
    if (Object.keys(found).length) return;

    // Only the live rail's rows go up — a value typed on another tab stays behind.
    // Country goes up as its name, which is how the read carries it.
    const details = Object.fromEntries(
      fields.filter(isValue).map((field) => {
        const value = String(valueOf(field, values)).trim();
        return [field.name, field.kind === 'country' ? countryName(value) : value];
      }),
    );

    try {
      const result = await updatePayment({
        verification_type: method.verification_type,
        ...(method.transfers && { bank_wire: transfer.toUpperCase() }),
        ...details,
      }).unwrap();
      setEditing(false);
      // The endpoint words its own confirmation; ours only covers a body without one.
      toast.success(
        t(result?.message) || t('{{method}} saved', { method: t(method.name) }),
        t('We check new payout details within 1 business day.'),
      );
    } catch (err) {
      toast.error(t('Could not save {{method}}', { method: t(method.name) }), t(authErrorMessage(err)));
    }
  };

  const rejected = payment?.status?.toLowerCase() === 'rejected';

  return (
    <>
      <section className="pay-card">
        <div className="pay-card__head">
          <div>
            <h2 className="pay-card__title">{t('Payout method')}</h2>
            <p className="pay-card__note">
              {t('Choose where your performance fees are sent. One method covers every account.')}
            </p>
          </div>
        </div>

        <div className="pay-methods" role="radiogroup" aria-label={t('Payout method')}>
          {PAYMENT_METHODS.map((item) => {
            const isSelected = item.id === selected;
            return (
              <label
                key={item.id}
                className={[
                  'pay-method',
                  isSelected && 'is-selected',
                  item.comingSoon && 'is-soon',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <input
                  type="radio"
                  name="payout-method"
                  className="pay-method__input"
                  value={item.id}
                  checked={isSelected}
                  disabled={item.comingSoon}
                  onChange={() => choose(item.id)}
                />

                {item.comingSoon && <span className="pay-method__soon">{t('Coming soon')}</span>}
                {savedMethod === item.id && (
                  <span className="pay-method__onfile">{t('On file')}</span>
                )}

                <span className="pay-method__top">
                  <span className="pay-method__box" aria-hidden="true">
                    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="m2.6 6.2 2.2 2.2 4.6-4.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <MethodLogo id={item.id} />
                </span>

                <span className="pay-method__tagline">{t(item.tagline)}</span>
                <span className="pay-method__meta">
                  {item.meta.map((bit) => (
                    <span key={bit}>{t(bit)}</span>
                  ))}
                </span>
              </label>
            );
          })}
        </div>
      </section>

      <section className="pay-columns">
        <form className="pay-card pay-details" onSubmit={save} noValidate>
          <div className="pay-card__head">
            <div>
              <h2 className="pay-card__title">
                {method.transfers ? t("Recipient's bank details") : t('{{method}} details', { method: t(method.name) })}
              </h2>
              <p className="pay-card__note">
                {readOnly
                  ? t('The details we have on file.')
                  : t('The account name must match the name on your verified ID.')}
              </p>
            </div>
            {readOnly && <StatusChip>{t(payment.status)}</StatusChip>}
          </div>

          {method.transfers && !readOnly && (
            <SegmentedTabs
              block
              className="pay-transfers"
              value={transfer}
              onChange={switchTransfer}
              options={method.transfers.map((t) => ({ value: t.id, label: t.label }))}
            />
          )}

          {readOnly ? (
            <dl className="pay-summary">
              {savedTransfer && (
                <div className="pay-summary__row">
                  <dt>{t('Transfer type')}</dt>
                  <dd>{savedTransfer.label}</dd>
                </div>
              )}
              {savedFields.filter(isValue).map((field) => (
                <div key={field.name} className="pay-summary__row">
                  <dt>{t(field.label)}</dt>
                  <dd>{shown(field, savedDetails[field.name])}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <div className="pay-fields">
              {fields.map((field, i) => (
                <FieldRow
                  key={field.name ?? `${field.kind}-${i}`}
                  field={field}
                  values={values}
                  errors={errors}
                  onChange={change}
                />
              ))}
            </div>
          )}

          <div className="pay-details__foot">
            {readOnly ? (
              <>
                <p className="pay-hint">
                  {payment.is_verified
                    ? t('Payouts will be sent here.')
                    : rejected
                      ? t('We could not verify these details — edit them and save again.')
                      : t('We are checking these details — no need to resubmit.')}
                </p>
                <Button variant="toolbar" onClick={() => setEditing(true)}>
                  {t('Edit details')}
                </Button>
              </>
            ) : (
              <>
                <p className="pay-hint">
                  {onFile || !payment
                    ? t('Saving sends the details for a quick check.')
                    : t('Saving replaces {{method}} as your payout method.', {
                        method: t(methodById(savedMethod)?.name ?? 'the method on file'),
                      })}
                </p>
                <div className="pay-actions">
                  {payment && (
                    <Button variant="toolbar" onClick={cancel} disabled={isSaving}>
                      {t('Cancel')}
                    </Button>
                  )}
                  <Button variant="submit" htmlType="submit" disabled={isSaving}>
                    {isSaving ? t('Saving…') : method.transfers ? t('Confirm') : t('Save {{method}}', { method: t(method.name) })}
                  </Button>
                </div>
              </>
            )}
          </div>
        </form>

        <aside className="pay-card pay-info">
          <div className="pay-card__head">
            <h2 className="pay-card__title">{t('How payout verification works')}</h2>
          </div>

          <ol className="pay-steps">
            {HOW_IT_WORKS.map((step, i) => (
              <li key={step.title} className="pay-steps__item">
                <span className="pay-steps__number">{i + 1}</span>
                <div>
                  <p className="pay-steps__title">{t(step.title)}</p>
                  <p className="pay-steps__desc">{t(step.desc)}</p>
                </div>
              </li>
            ))}
          </ol>

          <p className="pay-info__note">
            {t('We never ask for card numbers or online banking passwords. Payout details are only used to send your performance fees.')}
          </p>
        </aside>
      </section>
    </>
  );
}

export default function PaymentVerification() {
  const { data, isLoading } = useGetPaymentVerificationQuery();
  // An empty `verification_type` means nothing is on file.
  const payment = data?.verification_type ? data : null;

  return (
    <div className="pay">
      <header className="pv-head">
        <div>
          <h1>{t('Payment Verification')}</h1>
          <p>
            {t('Tell us where to send your performance fees. We check the details once, and every payout after that goes straight there.')}
          </p>
        </div>
      </header>

      {isLoading ? (
        <PaymentSkeleton />
      ) : (
        <PaymentForm payment={payment} />
      )}
    </div>
  );
}
